import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./Notifications.module.scss";
import NotificationHeader from "../../../components/notificationsDrawer/NotificationHeader";
import RoundedTabs from "../../../components/roundedTabs/RoundedTabs";
import NotificationCard from "../../../components/notificationsDrawer/NotificationCard";
import { getNotifications } from "../../../store/actions/general.actions";
import { notificationsList } from "../../../store/selectors/general.selector";
import NotificationModals from "../../../components/notificationsDrawer/NotificationsModals";
import Skeleton from "react-loading-skeleton";
import { loading } from "../../../store/selectors/general.selector";
import { api as generalService } from '../../../store/services/general.service';
import { REDIRECTION_TYPES } from '../../../helpers/utils';
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const Notifications = () => {
  const dispatch = useDispatch();
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const notifications = useSelector(notificationsList);
  const [allNotifications, setNotifications] = useState(notifications);
  const [filteredNotifications, setFilteredNotifications] = useState(notifications);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notification, setNotification] = useState("");
  const [modalType, setModalType] = useState("");
  const isLoading = useSelector(loading);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);

  const cardRef = useRef(null);
  const history = useHistory();

  const transformLinks = (text, links) => {
    if (!text || text.trim() === "" || !text.includes("<@link:"))
      return text;

    const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
    const transformedString = text.replace(regex, (match, type, id) => {
      let entityName;
      if (links && links?.length > 0) {
        entityName = links.filter((e) => e.linkId === id).map((e) => e.linkName);
      }
      let linkUrl = "";
      if (type === "Client" || type === "Project" || type === "Scope")
        linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}s/${id}/overview`;
      else if (type === "Portfolio" || type === "Program")
        linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}/${id}/overview`;
      else if (type === "ChangeRequest" || type === "CommunicationPlan" || type === "Budget" || type === "Risk" || type === "Task")
        return `<a target="_blank" class="notification-redirection-link cursor-pointer" data-type="${type.toLowerCase()}" data-id="${id}">${entityName}</a>`;

      if (linkUrl !== "")
        return `<a href=${linkUrl} target="_self" data-type="${type}" data-id="${id}">${entityName}</a>`;
      else
        return `<span class="text-primary">${entityName}</span>`;
    });
    return transformedString;
  }

  useEffect(() => {
    const handleLinkClick = (e) => {
      const target = e.target;
      // Check if the clicked element has the redirection class
      if (target.classList.contains('notification-redirection-link')) {
        e.preventDefault();
        e.stopPropagation();

        const type = target.getAttribute('data-type');
        const id = target.getAttribute('data-id');

        if (type && id) {
          generalService.getNotificationLinkRedirectionRequest({ type, id })
            .then((response) => {
              if (response) {
                if (response.scopeId)
                  handleScopeRedirection(response.scopeId, id);

                else if (response.projectId)
                  handleProjectRedirection(response.projectId, type, id);

              }
            }).catch((error) => {
            });
        }
      }
    };

    const cardElement = cardRef.current;
    if (cardElement) {
      cardElement.addEventListener('click', handleLinkClick);
    }

    return () => {
      if (cardElement) {
        cardElement.removeEventListener('click', handleLinkClick);
      }
    };
  }, []);
  const handleScopeRedirection = (scopeId, taskId) => {
    history.push(`/scopes/${scopeId}/overview?r_id=${taskId}`);
  };

  const handleProjectRedirection = (projectId, type, id) => {
    if (projectId && type && id) {
      let redirectionType;
      switch (type) {
        case 'changerequest':
          redirectionType = REDIRECTION_TYPES.CHANGE_REQUEST;
          break;
        case 'communicationplan':
          redirectionType = REDIRECTION_TYPES.COMMUNICATION;
          break;
        case 'budget':
          redirectionType = REDIRECTION_TYPES.BUDGET;
          break;
        case 'risk':
          redirectionType = REDIRECTION_TYPES.RISK;
          break;
        case 'task':
          redirectionType = REDIRECTION_TYPES.TASK;
          break;
        default:
          return;
      }

      history.push(`/projects/${projectId}/overview?r_t=${redirectionType}&r_id=${id}`);
    }

  };

  const filterNotifications = (query, notifications) => {
    if (!query.trim()) return notifications;

    const lowerQuery = query.toLowerCase();
    return notifications.filter((n) => {
      const transformedTitle = transformLinks(n.title, n.links).toLowerCase();
      const transformedNotes = transformLinks(n.notes, n.links).toLowerCase();
      return (
        transformedTitle.includes(lowerQuery) ||
        transformedNotes.includes(lowerQuery)
      );
    });
  };

  const applyFilters = (query, notifications, unreadOnly) => {
    let filtered = filterNotifications(query, notifications);

    if (unreadOnly) {
      filtered = filtered.filter((n) => !n.isRead);
    }

    return filtered;
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = applyFilters(query, allNotifications, isFilterActive);
    setFilteredNotifications(filtered);
  };

  const handleFilterUnread = () => {
    const newFilterState = !isFilterActive;
    setIsFilterActive(newFilterState);
    const filtered = applyFilters(searchQuery, allNotifications, newFilterState);
    setFilteredNotifications(filtered);
  };

  useEffect(() => {
    if (notifications) {
      setNotifications(notifications);
      setFilteredNotifications(applyFilters(searchQuery, notifications, isFilterActive));
    }
  }, [notifications]);

  const tabs = [
    { label: "All Notifications", count: filteredNotifications.length },
  ];

  const panels = {
    "All Notifications": (
      <div className={styles.notificationsList}>
        {isLoading ? (
          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <div key={i} style={{ marginBottom: "12px" }}>
              <Skeleton
                height={80}
                baseColor="#e0e0e0"
                highlightColor="#ECE7FD"
              />
            </div>
          ))
        ) : filteredNotifications.length > 0 ? (
          filteredNotifications.map((item, index) => (
            <NotificationCard
              key={index}
              item={item}
              setIsModalOpen={setIsModalOpen}
              setNotification={setNotification}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <p className="text-sm-400 grey36Color text-center">
              {searchQuery || isFilterActive
                ? "No notifications found"
                : "No notifications yet"}
            </p>
          </div>
        )}
      </div>
    ),
  };

  useEffect(() => {
    dispatch(getNotifications());
  }, []);

  return (
    <>
      <div className={`${styles.mainWrapper} font-family-imp`}>
        <h3>Notifications</h3>
        <div className={styles.notifyDetailContainer}>
          <NotificationHeader
            headerBg={styles.headerBg}
            handleSearch={handleSearch}
            handleFilterUnread={handleFilterUnread}
            isFilterActive={isFilterActive}
          />
          <RoundedTabs
            tabs={tabs}
            panels={panels}
            panelStyle={styles.notifyPanel}
          />
        </div>
      </div>
      {isModalOpen && (
        <NotificationModals
          setIsModalOpen={setIsModalOpen}
          notification={notification}
          modalType={modalType}
        />
      )}
    </>
  );
};

export default Notifications;
